import { useMutation, UseMutationResult } from '@tanstack/react-query'

import { brand } from '@/utils/brand'
import { getV5ApiUrl } from '@/utils/urlHelpers'

export interface SessionPayload {
  login_id: string
  password: string
  cart_token?: string
}

export interface SessionResponse {
  id: string
  first_name: string
  last_name: string
  email: string
  phone_number: string
  auth_token: string
  image?: string | null
  digital_label: boolean
  terms: boolean
  pro_user: boolean
  mobile_verification_eligible: boolean
  mobile_verified: boolean
  roles: string[]
}

interface ErrorResponse {
  errors?: string[]
  message?: string
}

interface LogOutSessionResponse {
  success: boolean
}

interface LogOutSessionPayload {
  authToken: string
}

/**
 * Posts login credentials to create a new session
 *
 * @throws {Error} When login is invalid or request fails
 */
const postSessionsApi = async (payload: SessionPayload): Promise<SessionResponse> => {
  const sessionsURL = getV5ApiUrl('/sessions')

  const response = await fetch(sessionsURL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-brand-id': brand.key },
    body: JSON.stringify({ ...payload, frontend_app: true }),
  })

  const data = await response.json()

  if (!response.ok) {
    const errorData = data as ErrorResponse
    if (errorData.errors?.includes('invalid login')) {
      throw new Error('Invalid email or password')
    }
    throw new Error(errorData.message || 'Authentication failed')
  }

  return data as SessionResponse
}

const logOutSessionApi = async ({
  authToken,
}: LogOutSessionPayload): Promise<LogOutSessionResponse> => {
  const sessionsURL = getV5ApiUrl('/sessions')

  const response = await fetch(sessionsURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-brand-id': brand.key,
      'x-user-token': authToken,
    },
    body: JSON.stringify({ frontend_app: true, _method: 'DELETE' }),
  })

  const data = await response.json()

  return data as LogOutSessionResponse
}

/**
 * Hook for handling session creation via basic auth
 */
const useSessionsApi = (): UseMutationResult<SessionResponse, Error, SessionPayload, unknown> => {
  return useMutation<SessionResponse, Error, SessionPayload>({
    mutationFn: postSessionsApi,
  })
}

export const useLogOutSessionApi = (): UseMutationResult<
  LogOutSessionResponse,
  Error,
  LogOutSessionPayload,
  unknown
> => {
  return useMutation<LogOutSessionResponse, Error, LogOutSessionPayload, unknown>({
    mutationFn: logOutSessionApi,
  })
}

export default useSessionsApi
